// Composition de l'image partagée, en canvas 2D et SANS AUCUNE DÉPENDANCE.
//
// Pourquoi pas une bibliothèque de capture du DOM (html2canvas, html-to-image) : elle
// rejoue notre CSS dans son propre moteur, et ce qu'elle rend n'est pas ce que l'écran
// montre — elle ignore `container-query` (nos valeurs de tuiles sont en `clamp(…, 11cqi, …)`),
// les polices `@font-face` arrivent souvent trop tard, et elle sérialise le DOM en SVG dans
// un `<img>`, ce que notre CSP (`img-src`) doit alors autoriser. Surtout : capturer le DOM,
// c'est capturer ce qui EST dans le DOM — et le jour où une tuile payante y serait posée
// avec un flou purement visuel, la capture le retirerait. Ici on REDESSINE à partir des
// données déjà en mémoire, donc on ne peut dessiner que ce qu'on a le droit de lire.
//
// La contrepartie est assumée : l'image partagée n'est pas un pixel-perfect de l'écran,
// c'est une mise en page faite pour une messagerie — fond blanc, marges franches, gros
// caractères, largeur fixe. C'est ce qu'on veut : un DGS la regarde sur un téléphone.

import { NOM_PRODUIT } from '../../constants'

/** Largeur logique de l'image. 1200 px reste net dans une messagerie sans peser lourd. */
export const LARGEUR = 1200
/** On dessine à 2× puis on remet l'échelle : les caractères ne bavent pas sur un écran rétina. */
const ECHELLE = 2
export const MARGE = 64

// Les deux familles du site, servies par @fontsource depuis NOTRE domaine (aucun appel à
// fonts.googleapis.com — la CSP du Caddyfile bloque les polices tierces, et ce n'est pas
// un détail : elle a déjà bloqué des webfonts externes). Les familles de repli comptent :
// un canvas ne dit jamais qu'une police lui manque, il dessine en Times sans prévenir.
export const SERIF = "'IBM Plex Serif', Georgia, 'Times New Roman', serif"
export const SANS = "'IBM Plex Sans', system-ui, -apple-system, 'Segoe UI', Arial, sans-serif"

export const ENCRE = '#0D1F3C'        // marine de la marque
export const GRIS = '#5B6B84'
export const GRIS_CLAIR = '#98A4B8'
export const TRAIT = '#DCE3EE'
export const BLANC = '#FFFFFF'

// Espacements verticaux de la maquette — partagés avec la composition SVG (`svg.ts`), qui
// doit tomber au pixel près sur la même mise en page que le PNG.
export const ESPACE_BLOC = 18
/** Entre le dernier bloc et le filet du pied, puis entre le filet et la signature. */
export const ESPACE_AVANT_PIED = 36
export const ESPACE_APRES_FILET = 22
/** « © TerObs <année> — observatoire territorial » : la marque et le copyright voyagent avec
 *  l'image (Cyrille, 28/10). Aucune adresse : l'image remplace le lien (décision du 22/09). */
export const SIGNATURE = `© ${NOM_PRODUIT} ${new Date().getFullYear()} — observatoire territorial`

/** Un bloc de la maquette. La liste est volontairement courte : une image de partage qui
 *  demanderait un moteur de mise en page complet serait une page web, pas une image. */
export type Bloc =
  | { type: 'surtitre'; texte: string }
  | { type: 'titre'; texte: string }
  | { type: 'valeur'; texte: string; unite?: string }
  | { type: 'note'; texte: string }
  | { type: 'lignes'; lignes: { gauche: string; droite: string }[] }
  | { type: 'image'; source: CanvasImageSource; largeurSource: number; hauteurSource: number }
  | { type: 'espace'; hauteur: number }

export interface Police { taille: number; famille: string; graisse?: string; interligne: number }

export const POLICES: Record<'surtitre' | 'titre' | 'valeur' | 'unite' | 'note' | 'ligne' | 'pied', Police> = {
  surtitre: { taille: 22, famille: SANS, graisse: '600', interligne: 30 },
  titre: { taille: 40, famille: SERIF, graisse: '600', interligne: 50 },
  valeur: { taille: 84, famille: SERIF, graisse: '600', interligne: 96 },
  unite: { taille: 28, famille: SANS, graisse: '400', interligne: 36 },
  note: { taille: 21, famille: SANS, graisse: '400', interligne: 30 },
  ligne: { taille: 22, famille: SANS, graisse: '400', interligne: 34 },
  pied: { taille: 19, famille: SANS, graisse: '400', interligne: 26 },
}

export const fonte = (p: Police) => `${p.graisse ?? '400'} ${p.taille}px ${p.famille}`

/** Ce dont la mise en page a besoin pour mesurer : un contexte 2D, ou son substitut quand
 *  il n'y en a pas (la composition SVG sous jsdom, voir `svg.ts`). */
export type Mesureur = { font: string; measureText(texte: string): { width: number } }

/** Découpe un texte en lignes qui tiennent dans `largeur`. Coupe au mot ; un mot plus long
 *  que la ligne (une URL, un nom composé) est laissé tel quel plutôt que tronqué au milieu. */
export function replier(ctx: Mesureur, texte: string, largeur: number): string[] {
  const lignes: string[] = []
  for (const paragraphe of texte.split('\n')) {
    let courante = ''
    for (const mot of paragraphe.split(/\s+/).filter(Boolean)) {
      const essai = courante ? `${courante} ${mot}` : mot
      if (courante && ctx.measureText(essai).width > largeur) {
        lignes.push(courante)
        courante = mot
      } else {
        courante = essai
      }
    }
    lignes.push(courante)
  }
  return lignes.filter((l, i) => l !== '' || i === 0)
}

/** Hauteur d'un bloc, mesurée avec le MÊME contexte que celui du rendu — c'est la seule
 *  façon d'être sûr que la mesure et le tracé parlent de la même police. */
export function mesurer(ctx: Mesureur, bloc: Bloc, largeur: number): number {
  switch (bloc.type) {
    case 'espace': return bloc.hauteur
    case 'image': return Math.round(bloc.hauteurSource * (largeur / bloc.largeurSource))
    case 'valeur': return POLICES.valeur.interligne
    case 'lignes': return bloc.lignes.length * POLICES.ligne.interligne
    case 'surtitre':
    case 'titre':
    case 'note': {
      const p = POLICES[bloc.type]
      ctx.font = fonte(p)
      return replier(ctx, bloc.texte, largeur).length * p.interligne
    }
  }
}

function dessinerBloc(ctx: CanvasRenderingContext2D, bloc: Bloc, x: number, y: number,
                     largeur: number): void {
  switch (bloc.type) {
    case 'espace':
      return

    case 'image': {
      const h = Math.round(bloc.hauteurSource * (largeur / bloc.largeurSource))
      ctx.drawImage(bloc.source, x, y, largeur, h)
      // Un filet autour de l'image : sans lui, une carte claire ou un graphique à fond
      // blanc se fondent dans la marge et l'image semble mal cadrée.
      ctx.strokeStyle = TRAIT
      ctx.lineWidth = 1
      ctx.strokeRect(x + 0.5, y + 0.5, largeur - 1, h - 1)
      return
    }

    case 'valeur': {
      ctx.fillStyle = ENCRE
      ctx.font = fonte(POLICES.valeur)
      ctx.textBaseline = 'alphabetic'
      const ligneDeBase = y + POLICES.valeur.taille
      ctx.fillText(bloc.texte, x, ligneDeBase)
      if (bloc.unite) {
        // L'unité suit la valeur sur la même ligne de base, plus petite et grise : c'est
        // la hiérarchie de la tuile à l'écran, et elle porte du sens (« % », « €/hab. »).
        const apres = x + ctx.measureText(bloc.texte).width + 12
        ctx.fillStyle = GRIS
        ctx.font = fonte(POLICES.unite)
        ctx.fillText(bloc.unite, apres, ligneDeBase)
      }
      return
    }

    case 'lignes': {
      ctx.font = fonte(POLICES.ligne)
      ctx.textBaseline = 'alphabetic'
      let ligneDeBase = y + POLICES.ligne.taille
      for (const l of bloc.lignes) {
        ctx.fillStyle = GRIS
        ctx.textAlign = 'left'
        ctx.fillText(l.gauche, x, ligneDeBase)
        ctx.fillStyle = ENCRE
        ctx.textAlign = 'right'
        ctx.fillText(l.droite, x + largeur, ligneDeBase)
        ctx.textAlign = 'left'
        ligneDeBase += POLICES.ligne.interligne
      }
      return
    }

    case 'surtitre':
    case 'titre':
    case 'note': {
      const p = POLICES[bloc.type]
      ctx.font = fonte(p)
      ctx.textBaseline = 'alphabetic'
      ctx.fillStyle = bloc.type === 'surtitre' ? GRIS : bloc.type === 'note' ? GRIS : ENCRE
      let ligneDeBase = y + p.taille
      for (const ligne of replier(ctx, bloc.texte, largeur)) {
        ctx.fillText(ligne, x, ligneDeBase)
        ligneDeBase += p.interligne
      }
      return
    }
  }
}

export interface PiedDePage {
  /** Attributions de licence (fonds de carte, producteurs). Obligation, pas coquetterie. */
  attributions?: string
}

/**
 * Compose l'image et rend un PNG.
 *
 * L'image DOIT se suffire à elle-même : c'est ce qui la rend utile hors du site, et c'est
 * notre seule signature quand elle circule. Le pied de page n'est donc pas optionnel.
 */
export async function composerPng(blocs: Bloc[], pied: PiedDePage = {}): Promise<Blob> {
  await policesPretes()

  const largeurUtile = LARGEUR - MARGE * 2

  // Passe 1 — mesure, sur un contexte jetable réglé comme celui du rendu.
  const regle = document.createElement('canvas').getContext('2d')
  if (!regle) throw new Error('Canvas 2D indisponible : impossible de composer l’image.')

  const hauteurs = blocs.map(b => mesurer(regle, b, largeurUtile))
  const corps = hauteurs.reduce((s, h) => s + h, 0)
    + Math.max(0, blocs.length - 1) * ESPACE_BLOC

  // Chaque source WMS voyage avec sa licence : le pied peut occuper plusieurs lignes.
  regle.font = fonte(POLICES.pied)
  const attributions = pied.attributions ? replier(regle, pied.attributions, largeurUtile) : []
  const lignesPied = 1 + attributions.length
  const hauteurPied = 1 + ESPACE_APRES_FILET + lignesPied * POLICES.pied.interligne
  const hauteur = MARGE + corps + ESPACE_AVANT_PIED + hauteurPied + MARGE

  // Passe 2 — le rendu.
  const toile = document.createElement('canvas')
  toile.width = Math.round(LARGEUR * ECHELLE)
  toile.height = Math.round(hauteur * ECHELLE)
  const ctx = toile.getContext('2d')
  if (!ctx) throw new Error('Canvas 2D indisponible : impossible de composer l’image.')
  ctx.scale(ECHELLE, ECHELLE)

  // Fond blanc EXPLICITE : un canvas naît transparent, et une image PNG transparente
  // s'affiche en noir dans plusieurs messageries (mode sombre de WhatsApp, Outlook).
  ctx.fillStyle = BLANC
  ctx.fillRect(0, 0, LARGEUR, hauteur)

  let y = MARGE
  blocs.forEach((bloc, i) => {
    dessinerBloc(ctx, bloc, MARGE, y, largeurUtile)
    y += hauteurs[i] + (i < blocs.length - 1 ? ESPACE_BLOC : 0)
  })

  // Pied : filet, signature, attributions.
  y += ESPACE_AVANT_PIED
  ctx.strokeStyle = TRAIT
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.moveTo(MARGE, y + 0.5)
  ctx.lineTo(LARGEUR - MARGE, y + 0.5)
  ctx.stroke()
  y += ESPACE_APRES_FILET

  ctx.font = fonte(POLICES.pied)
  ctx.textBaseline = 'alphabetic'
  ctx.textAlign = 'left'
  ctx.fillStyle = GRIS
  // Signature sobre, sans adresse : la décision du 22/09 interdit qu'un lien circule.
  ctx.fillText(SIGNATURE, MARGE, y + POLICES.pied.taille)

  if (pied.attributions) {
    ctx.fillStyle = GRIS_CLAIR
    y += POLICES.pied.interligne
    for (const ligne of attributions) {
      ctx.fillText(ligne, MARGE, y + POLICES.pied.taille)
      y += POLICES.pied.interligne
    }
  }

  return versBlob(toile)
}

function versBlob(toile: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    if (typeof toile.toBlob !== 'function') {
      reject(new Error('Export PNG indisponible sur ce navigateur.'))
      return
    }
    toile.toBlob(b => b ? resolve(b) : reject(new Error('Le PNG n’a pas pu être produit.')),
                 'image/png')
  })
}

/**
 * Les polices `@font-face` se chargent à la demande. Un canvas, lui, ne dit jamais qu'une
 * police lui manque : il dessine en police de repli, sans prévenir — et on ne s'en aperçoit
 * qu'en regardant l'image reçue. On attend donc qu'elles soient prêtes, avec une borne :
 * mieux vaut une image en Georgia que pas d'image du tout.
 */
export async function policesPretes(): Promise<void> {
  const fontes = (document as Document & { fonts?: FontFaceSet }).fonts
  if (!fontes) return
  try {
    await Promise.race([
      Promise.all([
        fontes.load(`600 40px ${SERIF}`),
        fontes.load(`600 84px ${SERIF}`),
        fontes.load(`400 21px ${SANS}`),
        fontes.load(`600 22px ${SANS}`),
      ]),
      new Promise(r => setTimeout(r, 1500)),
    ])
  } catch {
    // Police indisponible : on dessine avec le repli plutôt que d'abandonner le partage.
  }
}

/** Retire le balisage d'une attribution servie en HTML (`basemap.ts` en pose pour les liens)
 *  et normalise les espaces : sur une image, un lien n'est plus cliquable, seul son nom compte. */
export function texteAttribution(html: string): string {
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim()
}
